import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  Lock,
  Save
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePictureUpload from '../ProfilePictureUpload';
import AccountLinker from '../AccountLinker';
import FavoriteTeamsSection from '../FavoriteTeamsSection';
import { UserProfileService } from '../../services/userProfileService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, updateEmail } from 'firebase/auth';
import { db } from '../../services/firebase';

const ProfileScreen = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [initialPhone, setInitialPhone] = useState('');
  const [initialFirstName, setInitialFirstName] = useState('');
  const [initialLastName, setInitialLastName] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoadingProfile(true);
        
        // Fetch user data from Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        let first = '';
        let last = '';
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Check if firstName and lastName exist in Firestore
          if (userData.firstName && userData.lastName) {
            first = userData.firstName;
            last = userData.lastName;
          } else {
            // Fallback: parse displayName for legacy users
            const nameParts = (userData.displayName || currentUser?.displayName || '').split(' ');
            first = nameParts[0] || '';
            last = nameParts.slice(1).join(' ') || '';
          }
          
          // Load phone from Firestore
          if (userData.phone) {
            setPhone(userData.phone);
            setInitialPhone(userData.phone);
          }
        } else {
          // No Firestore doc, use Auth displayName
          const nameParts = currentUser?.displayName?.split(' ') || [];
          first = nameParts[0] || '';
          last = nameParts.slice(1).join(' ') || '';
        }
        
        setFirstName(first);
        setLastName(last);
        setInitialFirstName(first);
        setInitialLastName(last);
        setEmail(currentUser?.email || '');
        setInitialEmail(currentUser?.email || '');
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Could not load all profile data');
      } finally {
        setLoadingProfile(false);
      }
    };

    if (currentUser) {
      loadProfile();
    }
  }, [currentUser]);

  // Auto-format phone number as user types
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    // Limit to 10 digits
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    
    // Format as xxx-xxx-xxxx
    let formatted = value;
    if (value.length > 6) {
      formatted = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
    } else if (value.length > 3) {
      formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
    }
    
    setPhone(formatted);
  };

  // Validate phone format
  const isPhoneValid = (phoneNumber) => {
    if (!phoneNumber) return true; // Empty is valid (optional field)
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    return phoneRegex.test(phoneNumber);
  };

  // Validate email format
  const isEmailValid = (emailAddress) => {
    if (!emailAddress) return false; // Email is required
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddress);
  };

  // Handle save
  const handleSave = async () => {
    setError('');
    setSuccess(false);

    // Validate names are not empty
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    // Validate email format
    if (!isEmailValid(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone format
    if (phone && !isPhoneValid(phone)) {
      setError('Please enter a valid phone number (xxx-xxx-xxxx)');
      return;
    }

    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const displayName = `${firstName} ${lastName}`;
      
      // Update email if it has changed and is unverified
      if (email !== initialEmail && !currentUser.emailVerified) {
        await updateEmail(currentUser, email);
      }
      
      // Update Firestore
      await updateDoc(userDocRef, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayName.trim(),
        phone: phone,
        profileUpdatedAt: new Date()
      });
      
      // Update Firebase Auth displayName
      await updateProfile(currentUser, {
        displayName: displayName.trim()
      });
      
      // Update initial values
      setInitialPhone(phone);
      setInitialFirstName(firstName);
      setInitialLastName(lastName);
      setInitialEmail(email);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle retry after error
  const handleRetry = () => {
    handleSave();
  };

  // Handle password change dialog
  const handlePasswordChange = () => {
    console.log('Change Password');
    setPasswordDialogOpen(false);
    // Reset dialog fields
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Check if phone has been modified
  const isPhoneModified = phone !== initialPhone;
  const isNameModified = firstName !== initialFirstName || lastName !== initialLastName;
  const isEmailModified = email !== initialEmail;
  const isModified = isPhoneModified || isNameModified || isEmailModified;

  if (loadingProfile) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/account')}
        >
          Back
        </Button>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Profile updated successfully!
        </Alert>
      )}

      {/* Error Message with Retry */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Profile Picture */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <ProfilePictureUpload
            size={100}
            sx={{ mb: 2 }}
            onUploadSuccess={() => {
              console.log('Profile picture updated');
            }}
            onUploadError={(error) => {
              setError('Failed to update profile picture: ' + error.message);
            }}
          />
          <Typography variant="h6" color="text.secondary">
            Profile Picture
          </Typography>
        </CardContent>
      </Card>

      {/* Profile Information Form */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Profile Information
          </Typography>

          {/* First Name - Editable */}
          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Last Name - Editable */}
          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Email - Editable if unverified */}
          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            InputProps={{
              readOnly: currentUser?.emailVerified,
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
            helperText={
              currentUser?.emailVerified 
                ? 'Email verified' 
                : 'Email not verified - you can update it'
            }
            error={email !== '' && !isEmailValid(email)}
            sx={{
              '& .MuiInputBase-input': {
                cursor: currentUser?.emailVerified ? 'default' : 'text'
              }
            }}
          />

          {/* Phone - Editable */}
          <TextField
            fullWidth
            label="Phone Number"
            value={phone}
            onChange={handlePhoneChange}
            margin="normal"
            placeholder="123-456-7890"
            helperText="US phone number format (optional)"
            error={phone !== '' && !isPhoneValid(phone)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Save Button */}
          <Button
            fullWidth
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={
              loading || 
              !isModified || 
              (phone !== '' && !isPhoneValid(phone)) || 
              !firstName.trim() || 
              !lastName.trim() ||
              !isEmailValid(email)
            }
            sx={{ mt: 2 }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Favorite Teams Section */}
      <FavoriteTeamsSection />

      {/* Linked Sportsbook Accounts Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Linked Sportsbook Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Track which sportsbooks you have accounts with for easier access.
          </Typography>
          <AccountLinker compact={true} />
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Security
          </Typography>

          {/* Change Password Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Lock />}
            onClick={() => setPasswordDialogOpen(true)}
          >
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordChange} 
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileScreen;
